/**
 * predictive-search.js — Predictive search overlay and <predictive-search> component
 */

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this._input = this.querySelector('.search-modal__input');
    this._results = this.querySelector('[data-search-results]');
    this._debounceTimer = null;
    this._controller = null;

    if (this._input) {
      this._input.addEventListener('input', this._onInput.bind(this));
      this._input.addEventListener('keydown', this._onKeydown.bind(this));
    }

    this.querySelector('[data-search-close]')?.addEventListener('click', this._close.bind(this));

    document.addEventListener(Summit.events.SEARCH_OPEN, this._open.bind(this));
    document.addEventListener(Summit.events.SEARCH_CLOSE, this._close.bind(this));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this._close();
    });

    this.querySelector('.search-modal')?.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) this._close();
    });
  }

  _open() {
    const modal = this.querySelector('.search-modal');
    modal?.classList.add('search-modal--open');
    setTimeout(() => this._input?.focus(), 50);
    document.body.style.overflow = 'hidden';
  }

  _close() {
    const modal = this.querySelector('.search-modal');
    modal?.classList.remove('search-modal--open');
    document.body.style.overflow = '';
    if (this._input) this._input.value = '';
    if (this._results) this._results.innerHTML = '';
  }

  _onInput() {
    clearTimeout(this._debounceTimer);
    const query = this._input.value.trim();
    if (query.length < 2) {
      if (this._results) this._results.innerHTML = '';
      return;
    }
    this._debounceTimer = setTimeout(() => this._fetch(query), 300);
  }

  async _fetch(query) {
    this._controller?.abort();
    this._controller = new AbortController();

    const url = `/search/suggest?q=${encodeURIComponent(query)}&resources[type]=product,collection&resources[limit]=5&section_id=predictive-search`;

    try {
      const response = await fetch(url, { signal: this._controller.signal });
      if (!response.ok) return;
      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      const results = doc.querySelector('[data-predictive-search-results]');
      if (this._results && results) {
        this._results.innerHTML = results.innerHTML;
      }
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Search error:', err);
    }
  }

  _onKeydown(e) {
    const items = this.querySelectorAll('.search-modal__result-item');
    const focused = this.querySelector('.search-modal__result-item:focus');
    const idx = [...items].indexOf(focused);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = items[idx + 1] || items[0];
      next?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = items[idx - 1] || items[items.length - 1];
      if (idx <= 0) { this._input?.focus(); return; }
      prev?.focus();
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
